// src/components/OnboardingTour.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  USAGE
//  -----
//  1. Import and drop into App.jsx (or wherever you gate first-time users):
//
//     import OnboardingTour from './components/OnboardingTour';
//
//  2. Show it after profile setup is complete for a new user:
//
//     const [showTour, setShowTour] = useState(user?.isNew === true);
//     // or persist the flag via profile API:
//     // const [showTour, setShowTour] = useState(!profile?.hasSeenTour);
//
//  3. Render it above your DashboardPage (it blurs whatever is behind it):
//
//     <>
//       <DashboardPage ... />
//       {showTour && (
//         <OnboardingTour onFinish={() => setShowTour(false)} />
//       )}
//     </>
//
//  4. (Optional) When the user finishes, persist the flag so it never shows again:
//
//     const handleFinish = async () => {
//       setShowTour(false);
//       await api('/profile', { method: 'PUT', body: JSON.stringify({ hasSeenTour: true, ...otherFields }) });
//     };
//     <OnboardingTour onFinish={handleFinish} />
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react';

// ── Inline styles — no external CSS dependency ────────────────────────────────
const S = {
  // Backdrop — fixed overlay with backdrop blur so DashboardPage shows through
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(5, 5, 7, 0.72)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    animation: 'obFadeIn .3s ease',
  },

  // Modal card
  modal: {
    position: 'relative',
    width: '100%',
    maxWidth: 640,
    maxHeight: 'calc(100vh - 32px)',
    background: 'linear-gradient(160deg, #0D0D11 0%, #0A0A0E 60%, #0C0C10 100%)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 40px 120px rgba(0,0,0,0.8), 0 0 0 1px rgba(201,168,76,0.08)',
    animation: 'obSlideUp .4s cubic-bezier(0.2,0,0,1)',
  },

  // Decorative glow top-right
  glowTR: {
    position: 'absolute',
    top: -100,
    right: -80,
    width: 340,
    height: 340,
    background: 'radial-gradient(ellipse, rgba(201,168,76,0.09) 0%, transparent 70%)',
    pointerEvents: 'none',
    borderRadius: '50%',
  },

  // Decorative glow bottom-left
  glowBL: {
    position: 'absolute',
    bottom: -60,
    left: -60,
    width: 260,
    height: 260,
    background: 'radial-gradient(ellipse, rgba(74,158,255,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
    borderRadius: '50%',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 22px 0',
    position: 'relative',
    zIndex: 2,
    flexShrink: 0,
  },

  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  logo: {
    width: 30,
    height: 30,
    background: 'rgba(201,168,76,0.12)',
    border: '1px solid rgba(201,168,76,0.28)',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: '#C9A84C',
    letterSpacing: '0.04em',
    fontFamily: 'system-ui, sans-serif',
  },

  brandName: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(240,234,214,0.45)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontFamily: 'system-ui, sans-serif',
  },

  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  skipBtn: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(240,234,214,0.35)',
    fontSize: 11,
    padding: '5px 13px',
    borderRadius: 20,
    cursor: 'pointer',
    letterSpacing: '0.04em',
    fontFamily: 'system-ui, sans-serif',
    transition: 'border-color .2s, color .2s',
  },

  closeBtn: {
    width: 28,
    height: 28,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '50%',
    cursor: 'pointer',
    color: 'rgba(240,234,214,0.35)',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color .2s, color .2s',
    lineHeight: 1,
    fontFamily: 'system-ui, sans-serif',
  },

  progressWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '14px 22px 0',
    position: 'relative',
    zIndex: 2,
    flexShrink: 0,
  },

  progTrack: {
    height: 3,
    flex: 1,
    borderRadius: 2,
    background: 'rgba(255,255,255,0.07)',
    overflow: 'hidden',
    cursor: 'pointer',
  },

  progFill: (pct) => ({
    height: '100%',
    width: pct + '%',
    background: 'linear-gradient(90deg, #C9A84C, #E8D080)',
    borderRadius: 2,
    transition: 'width .5s cubic-bezier(0.4,0,0.2,1)',
  }),

  stepCount: {
    fontSize: 10,
    color: 'rgba(240,234,214,0.3)',
    letterSpacing: '0.08em',
    whiteSpace: 'nowrap',
    marginLeft: 4,
    fontFamily: 'system-ui, sans-serif',
  },

  body: {
    flex: 1,
    padding: '22px 22px 16px',
    position: 'relative',
    zIndex: 2,
    overflowY: 'auto',
  },

  // Welcome slide
  welcomeWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    paddingTop: 8,
  },

  welcomeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    background: 'rgba(201,168,76,0.1)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 20,
    padding: '5px 14px',
    fontSize: 10,
    color: '#C9A84C',
    letterSpacing: '0.12em',
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: 18,
    fontFamily: 'system-ui, sans-serif',
  },

  welcomeTitle: {
    fontSize: 30,
    fontWeight: 800,
    lineHeight: 1.15,
    background: 'linear-gradient(135deg, #F0EAD6 20%, #C9A84C 90%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: 10,
    fontFamily: 'system-ui, sans-serif',
  },

  welcomeSub: {
    fontSize: 13,
    color: 'rgba(240,234,214,0.55)',
    lineHeight: 1.7,
    maxWidth: 420,
    marginBottom: 22,
    fontFamily: 'system-ui, sans-serif',
  },

  pillRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 7,
    justifyContent: 'center',
    marginBottom: 22,
  },

  pill: (color) => ({
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 20,
    padding: '4px 11px',
    fontSize: 11,
    color: 'rgba(240,234,214,0.45)',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontFamily: 'system-ui, sans-serif',
  }),

  pillDot: (color) => ({
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  }),

  // Step slides
  stepTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 10,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontWeight: 600,
    color: '#C9A84C',
    marginBottom: 10,
    fontFamily: 'system-ui, sans-serif',
  },

  tagDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: '#C9A84C',
  },

  slideTitle: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.2,
    background: 'linear-gradient(135deg, #F0EAD6 30%, #C9A84C 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: 8,
    fontFamily: 'system-ui, sans-serif',
  },

  slideSub: {
    fontSize: 13,
    color: 'rgba(240,234,214,0.5)',
    lineHeight: 1.65,
    marginBottom: 18,
    fontFamily: 'system-ui, sans-serif',
  },

  // Mock window
  mockup: {
    background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10,
    padding: '12px 14px',
    marginBottom: 16,
  },

  mockBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    marginBottom: 10,
    paddingBottom: 9,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },

  mockDot: (c) => ({ width: 7, height: 7, borderRadius: '50%', background: c }),
  mockTitle: {
    fontSize: 10,
    color: 'rgba(240,234,214,0.3)',
    marginLeft: 4,
    letterSpacing: '0.06em',
    fontFamily: 'system-ui, sans-serif',
  },

  // Dashboard stats row
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,1fr)',
    gap: 6,
  },

  stat: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 8,
    padding: '9px 10px',
    textAlign: 'center',
  },

  statVal: (c) => ({
    fontSize: 15,
    fontWeight: 700,
    color: c || '#C9A84C',
    fontFamily: 'system-ui, sans-serif',
  }),

  statLbl: {
    fontSize: 9,
    color: 'rgba(240,234,214,0.3)',
    letterSpacing: '0.06em',
    marginTop: 2,
    fontFamily: 'system-ui, sans-serif',
  },

  // Journal rows
  jRows: { display: 'flex', flexDirection: 'column', gap: 4 },

  jRow: {
    display: 'grid',
    gridTemplateColumns: '80px 50px 44px 1fr 68px',
    gap: 8,
    alignItems: 'center',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.04)',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 11,
    fontFamily: 'system-ui, sans-serif',
  },

  jPair: { fontWeight: 600, color: '#F0EAD6' },

  jDir: (buy) => ({
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 4,
    textAlign: 'center',
    background: buy ? 'rgba(61,190,115,0.14)' : 'rgba(217,79,79,0.12)',
    color: buy ? '#3DBE73' : '#D94F4F',
  }),

  jRR: { color: 'rgba(240,234,214,0.45)', fontSize: 11 },
  jStrat: { color: 'rgba(240,234,214,0.3)', fontSize: 10 },
  jPnl: (pos) => ({
    fontWeight: 600,
    textAlign: 'right',
    color: pos ? '#3DBE73' : '#D94F4F',
  }),

  // Feature grid
  featGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: 8,
    marginBottom: 18,
  },

  feat: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'default',
  },

  featIcon: (bg) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 9,
    fontSize: 14,
  }),

  featTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#F0EAD6',
    marginBottom: 3,
    fontFamily: 'system-ui, sans-serif',
  },

  featDesc: {
    fontSize: 10,
    color: 'rgba(240,234,214,0.35)',
    lineHeight: 1.5,
    fontFamily: 'system-ui, sans-serif',
  },

  // Checklist
  checklist: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },

  checkItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 11,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10,
    padding: '11px 13px',
  },

  checkNum: (bg, bc, c) => ({
    width: 22,
    height: 22,
    borderRadius: 6,
    background: bg || 'rgba(201,168,76,0.12)',
    border: `1px solid ${bc || 'rgba(201,168,76,0.28)'}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: c || '#C9A84C',
    flexShrink: 0,
    marginTop: 1,
    fontFamily: 'system-ui, sans-serif',
  }),

  checkLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#F0EAD6',
    marginBottom: 3,
    fontFamily: 'system-ui, sans-serif',
  },

  checkNote: {
    fontSize: 11,
    color: 'rgba(240,234,214,0.38)',
    lineHeight: 1.5,
    fontFamily: 'system-ui, sans-serif',
  },

  // Tip banner
  tip: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    background: 'rgba(201,168,76,0.06)',
    border: '1px solid rgba(201,168,76,0.22)',
    borderRadius: 10,
    padding: '11px 14px',
    marginBottom: 18,
  },

  tipIcon: { fontSize: 13, flexShrink: 0, marginTop: 1 },

  tipText: {
    fontSize: 11,
    color: 'rgba(240,234,214,0.55)',
    lineHeight: 1.55,
    fontFamily: 'system-ui, sans-serif',
  },

  // AI bubbles
  aiBubbles: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 },

  bubble: (isUser) => ({
    borderRadius: isUser ? '10px 10px 3px 10px' : '10px 10px 10px 3px',
    padding: '9px 13px',
    fontSize: 11,
    lineHeight: 1.55,
    maxWidth: '88%',
    alignSelf: isUser ? 'flex-end' : 'flex-start',
    background: isUser ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${isUser ? 'rgba(201,168,76,0.25)' : 'rgba(255,255,255,0.07)'}`,
    color: 'rgba(240,234,214,0.6)',
    fontFamily: 'system-ui, sans-serif',
  }),

  aiHighlight: { color: '#C9A84C', fontWeight: 600 },

  // Insight cards
  insightGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2,1fr)',
    gap: 8,
    marginBottom: 18,
  },

  insCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10,
    padding: '11px 13px',
  },

  insType: (c) => ({
    fontSize: 9,
    letterSpacing: '0.12em',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: c,
    marginBottom: 5,
    fontFamily: 'system-ui, sans-serif',
  }),

  insBody: {
    fontSize: 11,
    color: 'rgba(240,234,214,0.5)',
    lineHeight: 1.45,
    fontFamily: 'system-ui, sans-serif',
  },

  // Footer
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 22px 18px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    position: 'relative',
    zIndex: 2,
    flexShrink: 0,
  },

  dots: { display: 'flex', alignItems: 'center', gap: 5 },

  dot: (active) => ({
    height: 6,
    width: active ? 18 : 6,
    borderRadius: active ? 3 : '50%',
    background: active ? '#C9A84C' : 'rgba(255,255,255,0.12)',
    cursor: 'pointer',
    transition: 'all .2s',
    border: 'none',
    padding: 0,
  }),

  nav: { display: 'flex', alignItems: 'center', gap: 8 },

  btnBack: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.09)',
    color: 'rgba(240,234,214,0.38)',
    letterSpacing: '0.02em',
    fontFamily: 'system-ui, sans-serif',
    transition: 'border-color .2s, color .2s',
  },

  btnNext: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '8px 22px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #C9A84C, #B8942E)',
    border: '1px solid transparent',
    color: '#0A0A0B',
    letterSpacing: '0.02em',
    boxShadow: '0 4px 16px rgba(201,168,76,0.25)',
    fontFamily: 'system-ui, sans-serif',
    transition: 'box-shadow .2s, transform .15s',
  },

  btnFinish: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '8px 22px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #3DBE73, #2DA05A)',
    border: '1px solid transparent',
    color: '#0A0A0B',
    letterSpacing: '0.02em',
    boxShadow: '0 4px 16px rgba(61,190,115,0.25)',
    fontFamily: 'system-ui, sans-serif',
    transition: 'box-shadow .2s, transform .15s',
  },
};

// ── Keyframe injection (runs once) ────────────────────────────────────────────
const KEYFRAMES = `
@keyframes obFadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes obSlideUp { from { opacity: 0; transform: translateY(20px) scale(0.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
`;

function injectKeyframes() {
  if (document.getElementById('ob-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'ob-keyframes';
  style.textContent = KEYFRAMES;
  document.head.appendChild(style);
}

// ── Slide definitions ─────────────────────────────────────────────────────────
const TOTAL_STEPS = 6;

// ── Sub-components ────────────────────────────────────────────────────────────

function MockWindow({ title, children }) {
  return (
    <div style={S.mockup}>
      <div style={S.mockBar}>
        <div style={S.mockDot('#D94F4F')} />
        <div style={S.mockDot('#E2A542')} />
        <div style={S.mockDot('#3DBE73')} />
        <span style={S.mockTitle}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function FeatGrid({ items, cols = 3 }) {
  return (
    <div style={{ ...S.featGrid, gridTemplateColumns: `repeat(${cols},1fr)` }}>
      {items.map((f, i) => (
        <div key={i} style={S.feat}>
          <div style={S.featIcon(f.bg)}>{f.icon}</div>
          <div style={S.featTitle}>{f.title}</div>
          <div style={S.featDesc}>{f.desc}</div>
        </div>
      ))}
    </div>
  );
}

function CheckList({ items }) {
  return (
    <div style={S.checklist}>
      {items.map((c, i) => (
        <div key={i} style={S.checkItem}>
          <div style={S.checkNum(c.bg, c.bc, c.c)}>{c.num}</div>
          <div>
            <div style={S.checkLabel}>{c.label}</div>
            <div style={S.checkNote}>{c.note}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Tip({ icon, children }) {
  return (
    <div style={S.tip}>
      <span style={S.tipIcon}>{icon}</span>
      <span style={S.tipText}>{children}</span>
    </div>
  );
}

// ── Slide 0 — Welcome ─────────────────────────────────────────────────────────
function SlideWelcome() {
  const pills = [
    { label: 'Trade Journal',      color: '#3DBE73' },
    { label: 'Analytics',          color: '#4A9EFF' },
    { label: 'AI Coach',           color: '#C9A84C' },
    { label: 'Notebook',           color: '#A78BF5' },
    { label: 'Performance Charts', color: '#F472B6' },
  ];

  return (
    <div style={S.welcomeWrap}>
      <div style={S.welcomeBadge}>✦ Welcome aboard</div>
      <div style={S.welcomeTitle}>Your trading edge<br />starts here.</div>
      <div style={S.welcomeSub}>
        KFX Portal is your all-in-one trade journal, performance analytics suite,
        and AI coaching system — built for serious traders.
      </div>
      <div style={S.pillRow}>
        {pills.map((p) => (
          <div key={p.label} style={S.pill(p.color)}>
            <div style={S.pillDot(p.color)} />
            {p.label}
          </div>
        ))}
      </div>
      <Tip icon="💡">
        This quick tour takes about <strong style={{ color: '#C9A84C' }}>2 minutes</strong> and
        walks you through every key feature — from logging your first trade to unlocking
        AI-generated insights.
      </Tip>
    </div>
  );
}

// ── Slide 1 — Dashboard ───────────────────────────────────────────────────────
function SlideDashboard() {
  return (
    <>
      <div style={S.stepTag}><div style={S.tagDot} />Step 1 of 5 — Dashboard</div>
      <div style={S.slideTitle}>Your command center</div>
      <div style={S.slideSub}>
        The Dashboard gives you a live snapshot of your trading performance.
        Stats update automatically every time you log a trade.
      </div>
      <MockWindow title="KFX Dashboard">
        <div style={S.statsRow}>
          {[
            { val: '+$4,280', lbl: 'Net P&L',   color: '#3DBE73' },
            { val: '68.4%',   lbl: 'Win Rate',   color: null },
            { val: '2.4R',    lbl: 'Avg R:R',    color: null },
            { val: '7 🔥',    lbl: 'Win Streak', color: '#C9A84C' },
          ].map((s) => (
            <div key={s.lbl} style={S.stat}>
              <div style={S.statVal(s.color)}>{s.val}</div>
              <div style={S.statLbl}>{s.lbl}</div>
            </div>
          ))}
        </div>
      </MockWindow>
      <FeatGrid cols={3} items={[
        { icon: '📊', bg: 'rgba(61,190,115,0.12)',  title: 'Live stats',     desc: 'Net P&L, win rate, profit factor & drawdown — always current' },
        { icon: '🛑', bg: 'rgba(217,79,79,0.12)',   title: 'Pattern alerts', desc: 'Detects tilt, revenge sizing, and loss streaks in real time' },
        { icon: '📄', bg: 'rgba(201,168,76,0.1)',   title: 'PDF export',     desc: 'One-click performance report — shareable & print-ready' },
      ]} />
    </>
  );
}

// ── Slide 2 — Journal ─────────────────────────────────────────────────────────
function SlideJournal() {
  const rows = [
    { pair: 'XAUUSD', buy: true,  rr: '2.8R', strat: 'London Break',       pnl: '+$840', pos: true  },
    { pair: 'EURUSD', buy: false, rr: '1.2R', strat: 'OB Rejection',        pnl: '+$310', pos: true  },
    { pair: 'GBPJPY', buy: true,  rr: '—',    strat: 'Trend Continuation',  pnl: '-$180', pos: false },
  ];

  return (
    <>
      <div style={S.stepTag}><div style={S.tagDot} />Step 2 of 5 — Trade Journal</div>
      <div style={S.slideTitle}>Log every trade, in detail</div>
      <div style={S.slideSub}>
        The Journal is the core of KFX. Log trades manually, import from MT4/MT5,
        or paste a CSV. Each trade captures everything you need for a real review.
      </div>
      <MockWindow title="Journal — Recent Trades">
        <div style={S.jRows}>
          <div style={{ ...S.jRow, background: 'transparent', border: 'none', fontSize: 9, color: 'rgba(240,234,214,0.25)', letterSpacing: '0.07em', padding: '3px 10px' }}>
            <span>PAIR</span><span>DIR</span><span>R:R</span><span>STRATEGY</span><span style={{ textAlign: 'right' }}>P&L</span>
          </div>
          {rows.map((r) => (
            <div key={r.pair + r.pnl} style={S.jRow}>
              <span style={S.jPair}>{r.pair}</span>
              <span style={S.jDir(r.buy)}>{r.buy ? 'BUY' : 'SELL'}</span>
              <span style={S.jRR}>{r.rr}</span>
              <span style={S.jStrat}>{r.strat}</span>
              <span style={S.jPnl(r.pos)}>{r.pnl}</span>
            </div>
          ))}
        </div>
      </MockWindow>
      <CheckList items={[
        { num: '1', label: 'Click "+ Add Trade" in the Journal tab',    note: 'Fill in pair, direction, entry, SL, TP, lot size and P&L — all optional fields can be added later' },
        { num: '2', label: 'Add your post-trade review',                note: 'Write what went well, what didn\'t, and attach a chart screenshot for reference' },
        { num: '3', label: 'Or import via CSV / MT4 / MT5',             note: 'Use the Import button to bulk-upload up to 500 historical trades at once' },
      ]} />
    </>
  );
}

// ── Slide 3 — Analytics ───────────────────────────────────────────────────────
function SlideAnalytics() {
  return (
    <>
      <div style={S.stepTag}><div style={S.tagDot} />Step 3 of 5 — Analytics & Charts</div>
      <div style={S.slideTitle}>Understand your patterns</div>
      <div style={S.slideSub}>
        The Analytics and Charts pages turn your trade data into actionable insights —
        by session, strategy, pair, emotion, and more.
      </div>
      <FeatGrid cols={2} items={[
        { icon: '📈', bg: 'rgba(74,158,255,0.1)',    title: 'Session breakdown',   desc: 'London, New York, Asia — see exactly where you make and lose money' },
        { icon: '🧠', bg: 'rgba(167,139,245,0.1)',   title: 'Psychology tracking', desc: 'Emotion, discipline, and confidence scores logged per trade' },
        { icon: '🎯', bg: 'rgba(61,190,115,0.1)',    title: 'Strategy analysis',   desc: 'Compare win rates, RR, and frequency per setup type' },
        { icon: '⚡', bg: 'rgba(226,165,66,0.1)',    title: 'Performance score',   desc: 'Composite score from win rate, RR, streak, and consistency' },
      ]} />
      <Tip icon="💡">
        The more trades you log, the more accurate your analytics become. Aim to log at
        least <strong style={{ color: '#C9A84C' }}>20 trades</strong> before drawing
        conclusions from the data.
      </Tip>
    </>
  );
}

// ── Slide 4 — AI Coach ────────────────────────────────────────────────────────
function SlideAI() {
  return (
    <>
      <div style={S.stepTag}><div style={S.tagDot} />Step 4 of 5 — AI Coach</div>
      <div style={S.slideTitle}>Your personal trading coach</div>
      <div style={S.slideSub}>
        The AI Coach analyzes your full trade history, identifies weaknesses, and gives
        you specific, data-backed coaching — not generic advice.
      </div>
      <MockWindow title="AI Coach — Conversation">
        <div style={S.aiBubbles}>
          <div style={S.bubble(true)}>
            Analyze my last 30 trades and tell me where I'm losing edge.
          </div>
          <div style={S.bubble(false)}>
            <span style={S.aiHighlight}>Critical pattern found: </span>
            Your New York session trades have a 38% win rate vs 71% in London. You're also
            taking 40% larger lot sizes after losses — classic revenge trading. I'd suggest
            pausing NY entries until you audit those setups.
          </div>
        </div>
      </MockWindow>
      <div style={S.insightGrid}>
        {[
          { type: '✓ Strength', color: '#3DBE73', body: 'London session win rate is consistently above 65% across all strategies' },
          { type: '⚠ Warning',  color: '#E2A542', body: 'Lot size increases 38% after 2+ consecutive losses' },
          { type: '✗ Critical', color: '#D94F4F', body: 'NY close trades have -1.2R average — consider removing from plan' },
          { type: '✓ Strength', color: '#3DBE73', body: 'OB rejection setups show 2.8R average — your best edge' },
        ].map((c, i) => (
          <div key={i} style={S.insCard}>
            <div style={S.insType(c.color)}>{c.type}</div>
            <div style={S.insBody}>{c.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Slide 5 — Notebook & Profile ──────────────────────────────────────────────
function SlideNotebook() {
  return (
    <>
      <div style={S.stepTag}><div style={S.tagDot} />Step 5 of 5 — Notebook & Profile</div>
      <div style={S.slideTitle}>Complete your setup</div>
      <div style={S.slideSub}>
        Two final tools that tie everything together — a trading notebook for strategies
        and plans, and a profile that personalizes your entire experience.
      </div>
      <CheckList items={[
        {
          num: '📒',
          bg: 'rgba(167,139,245,0.1)', bc: 'rgba(167,139,245,0.28)', c: '#A78BF5',
          label: 'Notebook — your trading playbook',
          note: 'Write and organize your setups, rules, and weekly plans. Supports categories, pinning, and full-text search across all notes.',
        },
        {
          num: '👤',
          bg: 'rgba(74,158,255,0.1)', bc: 'rgba(74,158,255,0.25)', c: '#4A9EFF',
          label: 'Profile — set your trading rules',
          note: 'Add your trading rules and the AI Coach will track rule adherence on every trade you log. Account size and goals drive daily loss and profit targets.',
        },
        {
          num: '🎯',
          bg: 'rgba(61,190,115,0.1)', bc: 'rgba(61,190,115,0.25)', c: '#3DBE73',
          label: 'Set your goals',
          note: 'Enter account size, max daily loss %, weekly profit target, and max trades per day. The Dashboard tracks progress against these live.',
        },
      ]} />
      <Tip icon="🚀">
        You're all set. Start by logging your first trade in the{' '}
        <strong style={{ color: '#C9A84C' }}>Journal</strong> tab, then explore the
        Dashboard to watch your stats come to life.
      </Tip>
    </>
  );
}

const SLIDES = [SlideWelcome, SlideDashboard, SlideJournal, SlideAnalytics, SlideAI, SlideNotebook];

// ── Main component ────────────────────────────────────────────────────────────
export default function OnboardingTour({ onFinish }) {
  const [step, setStep] = useState(0);

  useEffect(() => { injectKeyframes(); }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onFinish?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onFinish]);

  const goTo = useCallback((n) => {
    setStep(Math.max(0, Math.min(TOTAL_STEPS - 1, n)));
  }, []);

  const isFirst = step === 0;
  const isLast  = step === TOTAL_STEPS - 1;

  const CurrentSlide = SLIDES[step];

  return (
    <div style={S.backdrop} onClick={onFinish} role="dialog" aria-modal="true" aria-label="KFX Portal onboarding tour">
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>

        {/* Decorative glows */}
        <div style={S.glowTR} />
        <div style={S.glowBL} />

        {/* Header */}
        <div style={S.header}>
          <div style={S.brand}>
            <div style={S.logo}>KFX</div>
            <div style={S.brandName}>Portal</div>
          </div>
          <div style={S.headerActions}>
            <button style={S.skipBtn} onClick={onFinish}>Skip tour</button>
            <button style={S.closeBtn} onClick={onFinish} aria-label="Close tour">✕</button>
          </div>
        </div>

        {/* Progress bar */}
        <div style={S.progressWrap}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div key={i} style={S.progTrack} onClick={() => goTo(i)} role="button" aria-label={`Go to step ${i + 1}`}>
              <div style={S.progFill(i <= step ? 100 : 0)} />
            </div>
          ))}
          <span style={S.stepCount}>{step + 1} / {TOTAL_STEPS}</span>
        </div>

        {/* Slide body */}
        <div style={S.body} key={step}>
          <CurrentSlide />
        </div>

        {/* Footer */}
        <div style={S.footer}>
          {/* Dot indicators */}
          <div style={S.dots}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <button
                key={i}
                style={S.dot(i === step)}
                onClick={() => goTo(i)}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div style={S.nav}>
            {!isFirst && (
              <button style={S.btnBack} onClick={() => goTo(step - 1)}>← Back</button>
            )}
            {isLast ? (
              <button style={S.btnFinish} onClick={onFinish}>
                🎉 Start Trading
              </button>
            ) : (
              <button style={S.btnNext} onClick={() => goTo(step + 1)}>
                {isFirst ? 'Start Tour →' : 'Next →'}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
